import { Dropdown, Icons, Menu, MenuButton, MenuItem, Stack, Typography } from '@healthinal/ui';
import { createFileRoute } from '@tanstack/react-router';
import { differenceInMilliseconds } from 'date-fns';
import { useState } from 'react';
import {
  useGetClinicalReportByPatientSuspense,
  useGetLabReportsByPatientSuspense,
} from '../../../api/generated/diagnosis-controller/diagnosis-controller.ts';
import { useGetLabOrdersByPatientSuspense } from '../../../api/generated/lab-order-controller/lab-order-controller.ts';
import { LabOrderDto, LabReportDto } from '../../../api/generated/model';
import { useGetTherapyByPatientSuspense } from '../../../api/generated/patient-controller/patient-controller.ts';
import { compareLabOrdersByLatest } from '../../../utils/labOrder.ts';
import { LabOrderCard } from '../../components/lab-order-results/LabOrderCard.tsx';
import { SelectAppointmentForLabOrderResultDialog } from '../../components/lab-order-results/SelectAppointmentForLabOrderResultDialog.tsx';
import { ClinicalReportCard } from './components/diagnostics/ClinicalReportCard.tsx';
import { CreateClinicalReportDialog } from './components/diagnostics/CreateClinicalReportDialog.tsx';
import { CreateLabReportDialog } from './components/diagnostics/CreateLabReportDialog.tsx';
import { DiagnosisSummaryCard } from './components/diagnostics/DiagnosisSummaryCard.tsx';
import { LabReportCard } from './components/diagnostics/LabReportCard.tsx';

export const Route = createFileRoute('/patients/$patientId/diagnostics')({
  component: Diagnostics,
  staticData: { title: 'Diagnostik' },
});

function Diagnostics() {
  const { patientId } = Route.useParams();
  const { data: clinicalReport } = useGetClinicalReportByPatientSuspense(patientId);
  const { data: therapy } = useGetTherapyByPatientSuspense(patientId);
  const { data: labReports } = useGetLabReportsByPatientSuspense(patientId);
  const { data: labOrders } = useGetLabOrdersByPatientSuspense(patientId);
  const [openAddFindingsDialog, setOpenAddFindingsDialog] = useState<(typeof menuItems)[number]['id'] | undefined>();
  const closeAddFindingsDialog = () => setOpenAddFindingsDialog(undefined);

  const sortedListItems: (LabReportDto | LabOrderDto)[] = [...labReports, ...labOrders];
  sortedListItems.sort((a, b) => {
    const samplingDateComparison = differenceInMilliseconds(b.samplingDate, a.samplingDate);

    if (samplingDateComparison !== 0) return samplingDateComparison;
    if (isLabOrder(a) && isLabOrder(b)) return compareLabOrdersByLatest(a, b);

    return differenceInMilliseconds(b.created ?? 0, a.created ?? 0);
  });

  return (
    <>
      <DiagnosisSummaryCard therapy={therapy} clinicalReport={clinicalReport} labReports={labReports} />
      <Stack direction="row" alignItems="start" justifyContent="space-between">
        <Typography level={'h3'}>Befunde</Typography>
        <Dropdown>
          <MenuButton color={'primary'} variant={'solid'} endDecorator={<Icons.Add />}>
            Neuer Befund
          </MenuButton>
          <Menu
            placement="bottom-end"
            sx={{
              '--List-padding': '0.5rem',
              '--ListItem-gap': '0.5rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 220px)',
              gap: 1,
            }}>
            {menuItems.map(({ id, icon, title, description }) => (
              <MenuItem
                key={id}
                onClick={() => setOpenAddFindingsDialog(id)}
                color="primary"
                disabled={id === 'mkb' && !!clinicalReport.patientId}
                sx={{ borderRadius: 'sm', p: 2.5, alignItems: 'start', justifyContent: 'start' }}
                orientation="vertical"
                variant="soft">
                {icon}
                <Stack>
                  <Typography level="title-md">{title}</Typography>
                  <Typography level="body-xs">{description}</Typography>
                </Stack>
              </MenuItem>
            ))}
          </Menu>
        </Dropdown>
      </Stack>
      {clinicalReport && <ClinicalReportCard report={clinicalReport} />}
      {sortedListItems.length > 0 &&
        sortedListItems.map((listItem) =>
          'labTestMaterial' in listItem ? (
            <LabReportCard key={listItem.id} report={listItem} />
          ) : (
            <LabOrderCard key={listItem.id} order={listItem} />
          ),
        )}
      {openAddFindingsDialog}
      <CreateLabReportDialog
        open={openAddFindingsDialog === 'mlb'}
        onClose={closeAddFindingsDialog}
        patientId={patientId}
      />
      <CreateClinicalReportDialog
        open={openAddFindingsDialog === 'mkb'}
        onClose={closeAddFindingsDialog}
        patientId={patientId}
      />
      <SelectAppointmentForLabOrderResultDialog
        patientId={patientId}
        open={openAddFindingsDialog === 'test'}
        onClose={closeAddFindingsDialog}
      />
    </>
  );
}

function isLabOrder(item: LabReportDto | LabOrderDto): item is LabOrderDto {
  return 'type' in item;
}

const menuItems = [
  {
    id: 'mkb',
    title: 'MKB',
    description: 'Meldung zum klinischen Befund',
    icon: <Icons.MedicalInformation />,
  },
  {
    id: 'mlb',
    title: 'MLB',
    description: 'Meldung zum laboranalytischen Befund',
    icon: <Icons.Science />,
  },
  {
    id: 'test',
    title: 'Test',
    description: 'Testresultat erfassen',
    icon: <Icons.Bloodtype />,
  },
] as const;
